import React, {useEffect, useState} from 'react';

import styled from 'styled-components';

import {colors} from '@app/styles/theme';

export interface SwitchProps {
    onChange?: (checked: boolean) => void;
    disabled?: boolean;
    label?: string;
    className?: string;
    id?: string;
    defaultVal?: boolean;
    name?: string;
    width?: string;
    value?: boolean;
    font?: {size: string; color: string};
}
const Container = styled.label<{
    $disabled: boolean;
    $width: string;
    font?: {size: string; color: string};
}>`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    width: ${({$width}) => $width};
    gap: 10px;
    cursor: ${({$disabled}) => ($disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({$disabled}) => ($disabled ? 0.6 : 1)};
`;

const SwitchRoot = styled.div<{$checked: boolean}>`
    position: relative;
    width: 52px;
    height: 28px;
    background: ${({$checked}) => ($checked ? '#6466E9' : '#E8E8FC')};
    border-radius: 24px;
    transition: background-color 0.2s ease;
`;

const Thumb = styled.div<{$checked: boolean}>`
    position: absolute;
    top: 4px;
    left: 4px;
    width: 20px;
    height: 20px;
    background: #ffffff;
    border-radius: 50%;
    transition: transform 0.2s ease;
    transform: ${({$checked}) => ($checked ? 'translateX(24px)' : 'translateX(0)')};
`;

const LabelText = styled.span<{font?: {size: string; color: string}}>`
    font-size: ${({font}) => font?.size || '14px'};
    color: ${({font}) => colors[font?.color as keyof typeof colors] || colors.textPrimary};
    user-select: none;
`;

const HiddenInput = styled.input`
    position: absolute;
    opacity: 0;
    pointer-events: none;
`;

export const Switch: React.FC<SwitchProps> = ({
    onChange,
    disabled = false,
    label,
    defaultVal,
    className,
    width = 'auto',
    font,
    ...otherProps
}) => {
    const [on, setOn] = useState(defaultVal ?? false);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOn(e.target.checked);
        if (onChange) {
            onChange(e.target.checked);
        }
    };

    useEffect(() => {
        setOn(otherProps.value ?? false);
    }, [otherProps]);

    return (
        <Container $disabled={disabled} className={className} $width={width} font={font}>
            {label && <LabelText font={font}>{label}</LabelText>}
            <SwitchRoot $checked={on}>
                <Thumb $checked={on} />
                <HiddenInput
                    type="checkbox"
                    checked={on}
                    onChange={handleChange}
                    disabled={disabled}
                    aria-label={label || 'Toggle switch'}
                />
            </SwitchRoot>
        </Container>
    );
};
